// Combination table — one row per (LoanDisk × account × legal action × owner × diary × lists × old hold) with its rule.
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Wrench, Eye } from "lucide-react";
import { ACCOUNT_TONES, LEGAL_TONES } from "@/components/collections/JourneyLine";

const STATUS_META = {
  fix:    { label: "Fix",    tone: "bg-rose-50 text-rose-800 border-rose-300" },
  review: { label: "Review", tone: "bg-amber-50 text-amber-800 border-amber-300" },
  ok:     { label: "OK",     tone: "bg-emerald-50 text-emerald-800 border-emerald-300" },
};
const ACCOUNT_LABEL = { open: "Open", arrears: "Arrears", legal: "Legal", restructured: "Restructured", closed: "Closed" };
const LEGAL_LABEL = { none: "None", pre_action: "Pre-action", ready_for_handover: "Ready for handover", live: "Live", paused: "Paused", done: "Done" };
const pill = "inline-flex items-center text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap";
const Tick = ({ on, testid }) => on ? <Check className="w-3.5 h-3.5 text-emerald-700 mx-auto" data-testid={testid} /> : <span className="text-slate-300">—</span>;

export function AuditCombinations({ combos, status, onStatus, selected, onSelect, canPublish, onFix, fmtGBP }) {
  return (
    <Card data-testid="audit-combinations">
      <CardHeader className="py-2.5 px-4 border-b flex flex-row items-center gap-3">
        <CardTitle className="text-sm font-medium">{combos.length} combination{combos.length === 1 ? "" : "s"}</CardTitle>
        <div className="flex items-center gap-1 ml-2 text-[11px]">
          {["all", "fix", "review", "ok"].map(s => (
            <button key={s} type="button" onClick={() => onStatus(s)} data-testid={`audit-status-${s}`}
              className={`px-2 h-6 rounded border transition-colors ${status === s ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}>
              {s === "all" ? "All" : STATUS_META[s].label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-[11px] text-muted-foreground">Click a row to list its files below</span>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Status</th><th className="text-right px-2 py-2">Files</th><th className="text-right px-2 py-2">Balance</th>
              <th className="text-left px-2 py-2">LoanDisk</th><th className="text-left px-2 py-2">Account</th><th className="text-left px-2 py-2">Legal action</th>
              <th className="px-2 py-2">Legal owner</th><th className="px-2 py-2">Diary</th><th className="px-2 py-2">Worklist</th><th className="px-2 py-2">Tracker</th><th className="px-2 py-2">Old hold</th>
              <th className="text-left px-2 py-2 min-w-[280px]">Rule</th><th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {combos.length === 0 && <tr><td colSpan={13} className="py-8 text-center text-muted-foreground" data-testid="audit-combos-empty">No combinations in this view.</td></tr>}
            {combos.map(c => {
              const sm = STATUS_META[c.status] || STATUS_META.ok;
              const isSel = selected === c.key;
              return (
                <tr key={c.key} onClick={() => onSelect(c.key)} className={`border-t cursor-pointer hover:bg-slate-50 ${isSel ? "bg-indigo-50/60" : ""}`} data-testid={`audit-combo-row-${c.status}-${c.account}-${c.legal}`}>
                  <td className="px-3 py-2"><span className={`${pill} ${sm.tone}`}>{sm.label}</span></td>
                  <td className="px-2 py-2 text-right font-semibold tabular-nums">{c.count}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-600">{fmtGBP(c.balance)}</td>
                  <td className="px-2 py-2 text-slate-700">{c.ld}</td>
                  <td className="px-2 py-2"><span className={`${pill} ${ACCOUNT_TONES[c.account] || ACCOUNT_TONES.open}`}>{ACCOUNT_LABEL[c.account] || c.account}</span></td>
                  <td className="px-2 py-2">{c.legal === "none" ? <span className="text-slate-400 italic">none</span> : <span className={`${pill} ${LEGAL_TONES[c.legal] || LEGAL_TONES.live}`}>⚖ {LEGAL_LABEL[c.legal] || c.legal}</span>}</td>
                  <td className="px-2 py-2 text-center"><Tick on={c.legal_owner} /></td>
                  <td className="px-2 py-2 text-center"><Tick on={c.open_actions} /></td>
                  <td className="px-2 py-2 text-center"><Tick on={c.worklist} /></td>
                  <td className="px-2 py-2 text-center"><Tick on={c.tracker} /></td>
                  <td className="px-2 py-2 text-center">{c.hold ? <span className="text-amber-700 font-semibold">hold</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-2 text-slate-700 leading-snug">{c.rule}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {c.fix && c.status === "fix" ? (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-800 hover:bg-rose-50" disabled={!canPublish}
                        title={canPublish ? c.fix_label : "Publisher permission needed to run fixes"} onClick={() => onFix(c)} data-testid={`audit-fix-btn-${c.fix}-${c.account}-${c.legal}`}>
                        <Wrench className="w-3 h-3 mr-1" /> Fix {c.count}
                      </Button>
                    ) : c.status === "review" ? (
                      <Button size="sm" variant="ghost" className="h-7 text-[11px] text-amber-800" onClick={() => onSelect(c.key)} data-testid={`audit-review-btn-${c.account}-${c.legal}`}>
                        <Eye className="w-3 h-3 mr-1" /> Review {c.count}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
